import { useEffect, useRef, useState } from "react";

import { type AccountManagerModel, type SupportServiceModel } from "@doitintl/cmp-models";
import { type ModelReference } from "@doitintl/models-any";

import { useApiContext } from "../../api/context";
import { useCustomerContext } from "../../Context/CustomerContext";
import { productAlertList } from "../../Support/consts";
import { type Ticket } from "../../Support/types";

export const upload = async (files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("attachments", file);
  });

  try {
    const response = await fetch("/api/ava/upload", {
      method: "POST",
      body: formData,
    });

    const result = (await response.json()) as string[];
    return result;
  } catch (error) {
    return [];
  }
};

export const getAccountManagerEmail = async (ref: ModelReference<AccountManagerModel>) => {
  const doc = await ref.get();
  if (doc.data()) {
    return doc.data()?.email;
  }
};

export function extractAttachments(message: string): { cleanedMessage: string; attachments: string[] } {
  const attachmentRegex = /(https:\/\/firebasestorage\.googleapis\.com\/[^\s]+)/g;
  const attachments: string[] = [];
  let cleanedMessage = message;
  if (message.includes("$attachments:")) {
    // Check if the message contains attachments section
    const parts = message.split("$attachments:");
    cleanedMessage = parts[0].trim();

    // If there's an attachments section, process it
    if (parts.length > 1) {
      const attachmentText = parts[1].trim();
      const urls = attachmentText.match(attachmentRegex) || [];

      // Extract filenames from URLs based on new rule
      urls.forEach((url) => {
        // Extract the path part from the URL
        const nameMatch = url.match(/\/o\/([^?]+)/);
        if (nameMatch) {
          const fullPath = decodeURIComponent(nameMatch[1]);
          // Extract filename up to the timestamp part (which follows the pattern of _TIMESTAMP_)
          const fileNameMatch = fullPath.match(/(.+)_\d{13}_\d+\.\w+$/);
          const fileName = fileNameMatch ? fileNameMatch[1] : "unknown";
          attachments.push(fileName);
        }
      });
    }
  } else if (message.includes("$file-names:")) {
    const fileParts = message.split("$file-names:");
    if (fileParts.length > 1) {
      const fileNames = fileParts[1].trim();
      const fileNamesArray = fileNames.split(", ");
      attachments.push(...fileNamesArray);
      cleanedMessage = fileParts[0].trim();
    }
  } else {
    cleanedMessage = message;
  }

  return { cleanedMessage, attachments };
}

export const useRecentTickets = () => {
  const api = useApiContext();
  const { customerOrPresentationModeCustomer: customer } = useCustomerContext();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      const resp = await api.get(`/support/customers/${customer.id}/tickets?quickLoad=true`);
      if (!resp.data.tickets) {
        return;
      }
      const users = resp.data.users || [];
      const tickets = resp.data.tickets.map((ticket: Ticket) => {
        const u = users.find((user) => ticket.assignee_id === user.id);
        ticket.assignee = u;
        return ticket;
      });
      setTickets(tickets);
      setLoading(false);
    };
    fetchTickets();
  }, [customer.id, api]);

  return { tickets, loading };
};

export const openTicketInZendesk = (ticketId: number) => {
  const zendeskURL = `https://doitintl.zendesk.com/agent/tickets/${ticketId}`;
  window.open(zendeskURL, "_blank");
};

export const checkIfCanOpenUrgentTicket = (
  platform: string,
  cloudProduct: SupportServiceModel | null,

  isDCIEssentials: boolean
) => {
  if (!isDCIEssentials) {
    return true;
  }
  return !productAlertList.includes(platform) || cloudProduct?.name === "Report a bug";
};

/**
 * Removes <img> tags with base64-encoded images from the provided HTML content string.
 * @param content The HTML content string to clean.
 * @returns The content string with base64 images removed.
 */
export function removeBase64ImagesFromContent(content: string): string {
  // Regex to match <img ... src="data:image/...;base64,..." ... />
  return content.replace(/<img[^>]*src=["']data:image\/[a-zA-Z]+;base64,[^"'>]+["'][^>]*>/g, "");
}

export const useMeasureTextWidth = (fontSize: number) => {
  const canvasRef = useRef(document.createElement("canvas"));

  const measureTextWidth = (txt: string) => {
    const context = canvasRef.current.getContext("2d");

    if (context) {
      context.font = `${fontSize}px Roboto`;
      return context.measureText(txt).width + 40;
    }
    return 0;
  };

  return measureTextWidth;
};

export function isJapanese(text: string): boolean {
  // Japanese regex pattern that matches:
  // - Hiragana
  // - Katakana (including long vowel mark)
  // - Special Japanese characters
  const japaneseRegex = /[ぁ-ゔ]+|[ァ-ヴー]+|[々〆〤ヶ]+/u;

  // Check if the regex matches and specifically look for Japanese characters
  const matches = text.match(japaneseRegex);

  if (!matches) return false;

  return matches.length > 0;
}

export const getTextLength = (htmlString: string) => htmlString.replace(/<[^>]*>/g, "").length;
